import React, { useState, useEffect } from 'react';
import { apiGet, apiPost } from '../utils/api';
import { Card, CardHeader, CardBody, PageHeader } from '../components/ui';
import { FaWhatsapp, FaCopy, FaCheck, FaTimes, FaPlus } from 'react-icons/fa';
import { useModal } from '../context/ModalContext';

const defaultForm = {
  phoneNumber: '',
  welcomeMessage: 'Hi there! How can we help you?',
  brandColor: '#25D366',
  position: 'right',
  avatarUrl: '',
  bubbleText: 'Chat with us',
  quickReplies: [],
  businessHoursMessage: '',
  active: true,
  removeBranding: false,
};

export default function WhatsAppWidget() {
  const modal = useModal();
  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [newReply, setNewReply] = useState('');
  const [canRemoveBranding, setCanRemoveBranding] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    // Read prefill from sessionStorage if the user came from the public
    // walytic.com/free-tools/whatsapp-widget customizer. Field names from
    // the free tool (`phone`, `greeting`, `agentName`) are mapped to the
    // app's widget model (`phoneNumber`, `welcomeMessage`, `bubbleText`).
    // `prefill` and `agentName` from the public tool have no exact
    // counterparts and are mapped where possible.
    const readPrefill = () => {
      try {
        const raw = sessionStorage.getItem('walytic_widget_prefill');
        if (!raw) return null;
        // Unicode-safe inverse of the website's encodeConfig. The greeting
        // can include emoji that don't fit Latin1, so we go through bytes.
        const binary = atob(raw);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const decoded = JSON.parse(new TextDecoder().decode(bytes));
        return {
          phoneNumber: decoded.phone || '',
          welcomeMessage: decoded.greeting || '',
          brandColor: decoded.brandColor || '',
          position: decoded.position || '',
          bubbleText: decoded.agentName || '',
        };
      } catch {
        return null;
      }
    };

    const clearPrefill = () => {
      try { sessionStorage.removeItem('walytic_widget_prefill'); } catch {}
    };

    const load = async () => {
      try {
        const res = await apiGet('/api/widget/config');
        const existing = res?.success && res?.widget ? res.widget : null;
        const hasExistingWidget = !!(existing && existing.widgetId);
        const prefill = hasExistingWidget ? null : readPrefill();

        setCanRemoveBranding(!!res?.canRemoveBranding);

        if (existing) {
          setForm({
            phoneNumber: (prefill?.phoneNumber) || existing.phoneNumber || '',
            welcomeMessage: (prefill?.welcomeMessage) || existing.welcomeMessage || defaultForm.welcomeMessage,
            brandColor: (prefill?.brandColor) || existing.brandColor || defaultForm.brandColor,
            position: (prefill?.position) || existing.position || defaultForm.position,
            avatarUrl: existing.avatarUrl || '',
            bubbleText: (prefill?.bubbleText) || existing.bubbleText || defaultForm.bubbleText,
            quickReplies: existing.quickReplies || [],
            businessHoursMessage: existing.businessHoursMessage || '',
            active: existing.active !== false,
            removeBranding: !!existing.removeBranding,
          });
          if (existing.widgetId) {
            const apiOrigin = window.location.origin.replace('app.', 'api.');
            setEmbedCode(
              `<script src="${apiOrigin}/api/widget/${existing.widgetId}.js" async></script>`
            );
          }
        } else if (prefill) {
          // Brand-new user with prefill from the free-tool customizer.
          setForm((prev) => ({
            ...prev,
            phoneNumber: prefill.phoneNumber || prev.phoneNumber,
            welcomeMessage: prefill.welcomeMessage || prev.welcomeMessage,
            brandColor: prefill.brandColor || prev.brandColor,
            position: prefill.position || prev.position,
            bubbleText: prefill.bubbleText || prev.bubbleText,
          }));
        }

        if (prefill) clearPrefill();
      } catch (err) {
        // silently ignore load errors on mount
      }
    };
    load();
  }, []);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const addReply = () => {
    const trimmed = newReply.trim();
    if (!trimmed) return;
    if (form.quickReplies.length >= 5) {
      modal.alert('Maximum 5 quick replies allowed.', 'warning');
      return;
    }
    if (trimmed.length > 50) {
      modal.alert('Quick reply cannot exceed 50 characters.', 'warning');
      return;
    }
    set('quickReplies', [...form.quickReplies, trimmed]);
    setNewReply('');
  };

  const removeReply = (index) => {
    set('quickReplies', form.quickReplies.filter((_, i) => i !== index));
  };

  const handleReplyKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addReply();
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      modal.alert('Failed to copy to clipboard.', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await apiPost('/api/widget/config', form);
      if (res?.success) {
        if (res?.embedCode) {
          setEmbedCode(res.embedCode);
        } else if (res?.widget?.widgetId) {
          const apiOrigin = window.location.origin.replace('app.', 'api.');
          setEmbedCode(
            `<script src="${apiOrigin}/api/widget/${res.widget.widgetId}.js" async></script>`
          );
        }
        modal.alert('Widget configuration saved successfully!', 'success');
      } else {
        modal.alert(res?.message || 'Failed to save configuration.', 'error');
      }
    } catch (err) {
      modal.alert(err?.message || 'Failed to save widget configuration.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="WhatsApp Widget"
        subtitle="Create a chat widget for your website"
        guideUrl="/guide/whatsapp-widget"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column: Configuration */}
        <Card>
          <CardHeader>Widget Configuration</CardHeader>
          <CardBody>
            <form onSubmit={handleSubmit}>
              {/* Phone Number */}
              <div className="input-group">
                <label className="input-label">Phone Number</label>
                <input
                  className="input"
                  type="text"
                  placeholder="919876543210"
                  value={form.phoneNumber}
                  onChange={(e) => set('phoneNumber', e.target.value)}
                  required
                />
                <p className="input-hint">Include country code without +</p>
              </div>

              {/* Welcome Message */}
              <div className="input-group">
                <label className="input-label">Welcome Message</label>
                <input
                  className="input"
                  type="text"
                  placeholder="Hi there! How can we help you?"
                  value={form.welcomeMessage}
                  onChange={(e) => set('welcomeMessage', e.target.value)}
                />
              </div>

              {/* Brand Color + Position */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="input-group">
                  <label className="input-label">Brand Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.brandColor}
                      onChange={(e) => set('brandColor', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-surface-border p-0.5 bg-transparent"
                    />
                    <input
                      className="input flex-1"
                      type="text"
                      value={form.brandColor}
                      onChange={(e) => set('brandColor', e.target.value)}
                      maxLength={7}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label className="input-label">Position</label>
                  <div className="flex gap-2">
                    {['left', 'right'].map((pos) => (
                      <button
                        key={pos}
                        type="button"
                        onClick={() => set('position', pos)}
                        className="flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-all capitalize"
                        style={
                          form.position === pos
                            ? {
                                backgroundColor: form.brandColor,
                                color: '#ffffff',
                                borderColor: form.brandColor,
                              }
                            : {}
                        }
                      >
                        {pos}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Avatar URL */}
              <div className="input-group">
                <label className="input-label">Avatar URL</label>
                <input
                  className="input"
                  type="text"
                  placeholder="https://example.com/logo.png"
                  value={form.avatarUrl}
                  onChange={(e) => set('avatarUrl', e.target.value)}
                />
              </div>

              {/* Bubble Text */}
              <div className="input-group">
                <label className="input-label">Bubble Text</label>
                <input
                  className="input"
                  type="text"
                  placeholder="Chat with us"
                  value={form.bubbleText}
                  onChange={(e) => set('bubbleText', e.target.value)}
                />
                <p className="input-hint">Text shown next to the floating button</p>
              </div>

              {/* Quick Replies */}
              <div className="input-group">
                <label className="input-label">
                  Quick Replies{' '}
                  <span className="text-ink-muted text-xs font-normal">
                    ({form.quickReplies.length}/5)
                  </span>
                </label>
                <div className="flex gap-2">
                  <input
                    className="input flex-1"
                    type="text"
                    placeholder="Type a quick reply..."
                    value={newReply}
                    onChange={(e) => setNewReply(e.target.value)}
                    onKeyDown={handleReplyKeyDown}
                    maxLength={50}
                  />
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={addReply}
                    disabled={form.quickReplies.length >= 5}
                  >
                    <FaPlus size={12} />
                  </button>
                </div>
                {form.quickReplies.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.quickReplies.map((reply, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm border"
                      >
                        {reply}
                        <button
                          type="button"
                          onClick={() => removeReply(i)}
                          className="ml-1 text-ink-muted hover:text-red-500 transition-colors"
                        >
                          <FaTimes size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Business Hours Message */}
              <div className="input-group">
                <label className="input-label">Business Hours Message</label>
                <input
                  className="input"
                  type="text"
                  placeholder="We reply within 24 hours"
                  value={form.businessHoursMessage}
                  onChange={(e) => set('businessHoursMessage', e.target.value)}
                />
              </div>

              {/* Active Toggle */}
              <div className="input-group">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => set('active', e.target.checked)}
                    className="w-4 h-4 rounded"
                  />
                  <span className="input-label mb-0">Active</span>
                </label>
              </div>

              {/* Remove "Powered by Walytic.com" branding (Pro only) */}
              <div className="input-group">
                <label className={`flex items-start gap-3 ${canRemoveBranding ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}>
                  <input
                    type="checkbox"
                    checked={form.removeBranding}
                    onChange={(e) => set('removeBranding', e.target.checked)}
                    disabled={!canRemoveBranding}
                    className="w-4 h-4 rounded mt-0.5"
                  />
                  <span className="flex flex-col">
                    <span className="input-label mb-0 inline-flex items-center gap-2">
                      Remove &quot;Powered by Walytic.com&quot; footer
                      {!canRemoveBranding && (
                        <span className="text-2xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold uppercase tracking-wider">
                          Pro
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-ink-faint mt-0.5">
                      {canRemoveBranding
                        ? 'When on, the widget renders without the Walytic footer link.'
                        : 'Available on the Pro plan. Upgrade to white-label your widget.'}
                    </span>
                  </span>
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="btn-primary w-full"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save & Generate Code'}
              </button>

              {/* Embed Code */}
              {embedCode && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="input-label mb-0">Embed Code</label>
                    <button
                      type="button"
                      className="btn-secondary btn-sm flex items-center gap-1"
                      onClick={handleCopy}
                    >
                      {copied ? <FaCheck size={12} /> : <FaCopy size={12} />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="bg-gray-50 border border-surface-border rounded-lg p-3 text-xs font-mono text-ink-primary overflow-x-auto whitespace-pre-wrap break-all">
                    {embedCode}
                  </pre>
                  <p className="input-hint">Paste this code before the closing &lt;/body&gt; tag of your website</p>
                </div>
              )}
            </form>
          </CardBody>
        </Card>

        {/* Right column: Preview */}
        <Card>
          <CardHeader>Preview</CardHeader>
          <CardBody>
            <div
              className="relative bg-gray-100 rounded-xl overflow-hidden"
              style={{ minHeight: 500 }}
            >
              {/* Simulated website content */}
              <div className="p-6 opacity-30 select-none">
                <div className="h-4 bg-gray-300 rounded w-3/4 mb-3" />
                <div className="h-3 bg-gray-300 rounded w-full mb-2" />
                <div className="h-3 bg-gray-300 rounded w-5/6 mb-2" />
                <div className="h-3 bg-gray-300 rounded w-2/3 mb-6" />
                <div className="h-4 bg-gray-300 rounded w-1/2 mb-3" />
                <div className="h-3 bg-gray-300 rounded w-full mb-2" />
                <div className="h-3 bg-gray-300 rounded w-4/5" />
              </div>

              {/* Popup */}
              <div
                className="absolute bottom-20"
                style={{ [form.position === 'left' ? 'left' : 'right']: 16, width: 288 }}
              >
                <div className="bg-white rounded-xl shadow-xl overflow-hidden">
                  {/* Popup Header */}
                  <div
                    className="px-4 py-3 flex items-center gap-3"
                    style={{ backgroundColor: form.brandColor }}
                  >
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {form.avatarUrl ? (
                        <img
                          src={form.avatarUrl}
                          alt="avatar"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <FaWhatsapp size={20} color="#ffffff" />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm leading-tight">
                        Chat on WhatsApp
                      </p>
                      <p className="text-white/80 text-xs">Typically replies instantly</p>
                    </div>
                  </div>

                  {/* Popup Body */}
                  <div className="bg-[#f0f2f5] px-4 py-4 space-y-3">
                    {/* Welcome message bubble */}
                    {form.welcomeMessage && (
                      <div className="flex">
                        <div className="bg-white rounded-lg rounded-tl-none px-3 py-2 shadow-sm max-w-[85%]">
                          <p className="text-sm text-gray-800">{form.welcomeMessage}</p>
                        </div>
                      </div>
                    )}

                    {/* Business hours message */}
                    {form.businessHoursMessage && (
                      <p className="text-xs text-center text-gray-500">
                        {form.businessHoursMessage}
                      </p>
                    )}

                    {/* Quick reply pills */}
                    {form.quickReplies.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {form.quickReplies.map((reply, i) => (
                          <button
                            key={i}
                            type="button"
                            className="px-3 py-1 rounded-full text-xs border bg-white"
                            style={{ borderColor: form.brandColor, color: form.brandColor }}
                          >
                            {reply}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Popup Footer */}
                  <div className="border-t border-gray-100 px-3 py-2 flex items-center gap-2 bg-white">
                    <input
                      readOnly
                      className="flex-1 bg-gray-100 rounded-full px-3 py-1.5 text-xs text-gray-400 outline-none"
                      placeholder="Type a message..."
                    />
                    <button
                      type="button"
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: form.brandColor }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                    </button>
                  </div>

                  {/* Branding footer (hidden when removeBranding is enabled) */}
                  {!form.removeBranding && (
                    <div className="border-t border-gray-100 bg-white text-center py-1.5">
                      <span className="text-[10px] text-gray-400 font-medium tracking-wide">
                        Powered by Walytic.com
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Floating button */}
              <div
                className="absolute bottom-4 flex items-center gap-2"
                style={{ [form.position === 'left' ? 'left' : 'right']: 16 }}
              >
                {form.position === 'right' && form.bubbleText && (
                  <div className="bg-white rounded-full px-4 py-2 shadow-md text-sm font-medium text-gray-700 whitespace-nowrap">
                    {form.bubbleText}
                  </div>
                )}
                <div
                  className="flex items-center justify-center rounded-full shadow-lg flex-shrink-0"
                  style={{
                    width: 56,
                    height: 56,
                    backgroundColor: form.brandColor,
                  }}
                >
                  <FaWhatsapp size={28} color="#ffffff" />
                </div>
                {form.position === 'left' && form.bubbleText && (
                  <div className="bg-white rounded-full px-4 py-2 shadow-md text-sm font-medium text-gray-700 whitespace-nowrap">
                    {form.bubbleText}
                  </div>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
